import { useEffect, useRef, useState } from "react";
import { Image, Typography } from "antd";

interface ProfileConfig {
  avatar?: string;
  cover_image?: string;
  full_name?: string;
  title?: string;
  bio?: string;
  phone?: string;
  email?: string;
  address?: string;
  website?: string;
  facebook?: string;
  instagram?: string;
  linkedin?: string;
  tiktok?: string;
  intro_video?: string;
  gallery?: string[];
}

interface Props {
  config: ProfileConfig;
}

/* ─── Floating Particles ─── */
const FloatingParticles = () => {
  const [particles] = useState(() =>
    Array.from({ length: 20 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 12,
      duration: 10 + Math.random() * 15,
      size: 2 + Math.random() * 4,
      opacity: 0.1 + Math.random() * 0.3,
    })),
  );

  return (
    <>
      {particles.map((p) => (
        <div
          key={p.id}
          className="fixed pointer-events-none z-50"
          style={{
            left: `${p.left}%`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            borderRadius: "50%",
            background: "rgba(200, 170, 110, 0.6)",
            opacity: p.opacity,
            animation: `profile-rise ${p.duration}s linear ${p.delay}s infinite`,
          }}
        />
      ))}
    </>
  );
};

/* ─── Animated Section (scroll reveal) ─── */
const AnimatedSection = ({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setTimeout(() => setVisible(true), delay);
      },
      { threshold: 0.15 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        transition: "opacity 0.8s ease, transform 0.8s ease",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(30px)",
      }}
    >
      {children}
    </div>
  );
};

/* ─── Social Icon ─── */
const SocialLink = ({ href, icon, label }: { href: string; icon: string; label: string }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="profile-social-btn flex items-center gap-3 px-5 py-3 rounded-2xl w-full"
    style={{
      background: "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)",
      border: "1px solid rgba(255,255,255,0.08)",
      color: "rgba(255,255,255,0.75)",
      textDecoration: "none",
      transition: "all 0.3s ease",
    }}
  >
    <span className="text-xl" style={{ width: 28, textAlign: "center" }}>
      {icon}
    </span>
    <span
      style={{
        fontFamily: "'DM Sans', sans-serif",
        fontSize: "0.9rem",
        letterSpacing: "0.02em",
      }}
    >
      {label}
    </span>
    <span className="ml-auto" style={{ color: "rgba(200,170,110,0.5)", fontSize: "0.85rem" }}>
      →
    </span>
  </a>
);

/* ─── Contact Item ─── */
const ContactItem = ({ icon, value, href }: { icon: string; value: string; href?: string }) => {
  const content = (
    <div className="flex items-center gap-3 py-2">
      <span className="text-base" style={{ width: 24, textAlign: "center", opacity: 0.6 }}>
        {icon}
      </span>
      <span
        style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: "0.9rem",
          color: "rgba(255,255,255,0.65)",
        }}
      >
        {value}
      </span>
    </div>
  );

  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
        {content}
      </a>
    );
  }
  return content;
};

/* ─── Elegant Divider ─── */
const ElegantDivider = () => (
  <div className="flex items-center justify-center gap-3 py-8">
    <div
      className="h-px w-12"
      style={{ background: "linear-gradient(to right, transparent, rgba(200,170,110,0.3))" }}
    />
    <div
      style={{
        width: 6,
        height: 6,
        borderRadius: "50%",
        background: "rgba(200,170,110,0.4)",
        animation: "profile-pulse 3s ease-in-out infinite",
      }}
    />
    <div className="h-px w-12" style={{ background: "linear-gradient(to left, transparent, rgba(200,170,110,0.3))" }} />
  </div>
);

/* ═══════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════ */
const PublicProfile = ({ config }: Props) => {
  const socialLinks = [
    { key: "facebook", href: config.facebook, icon: "📘", label: "Facebook" },
    { key: "instagram", href: config.instagram, icon: "📸", label: "Instagram" },
    { key: "linkedin", href: config.linkedin, icon: "💼", label: "LinkedIn" },
    { key: "tiktok", href: config.tiktok, icon: "🎵", label: "TikTok" },
    { key: "website", href: config.website, icon: "🌐", label: "Website" },
  ].filter((s) => s.href);

  return (
    <div className="min-h-screen relative overflow-x-hidden" style={{ background: "#0c0c0f" }}>
      {/* Global Profile Styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=DM+Sans:wght@300;400;500&family=Italiana&display=swap');

        @keyframes profile-rise {
          0% { transform: translateY(105vh) scale(0); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateY(-10vh) scale(1); opacity: 0; }
        }
        @keyframes profile-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        @keyframes profile-pulse {
          0%, 100% { transform: scale(1); opacity: 0.4; }
          50% { transform: scale(1.4); opacity: 0.8; }
        }
        @keyframes profile-shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes profile-border-glow {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.7; }
        }
        @keyframes profile-scroll-dot {
          0% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(8px); }
        }
        @keyframes profile-avatar-ring {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .profile-gold-text {
          background: linear-gradient(90deg, #c8aa6e 0%, #e8d5a3 25%, #c8aa6e 50%, #e8d5a3 75%, #c8aa6e 100%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: profile-shimmer 4s linear infinite;
        }

        .profile-glass {
          background: linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.015) 100%);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid rgba(255,255,255,0.06);
        }

        .profile-cover-overlay {
          background: linear-gradient(180deg,
            rgba(12,12,15,0) 0%,
            rgba(12,12,15,0.2) 30%,
            rgba(12,12,15,0.7) 65%,
            #0c0c0f 100%
          );
        }

        .profile-social-btn:hover {
          background: linear-gradient(135deg, rgba(200,170,110,0.12) 0%, rgba(200,170,110,0.04) 100%) !important;
          border-color: rgba(200,170,110,0.2) !important;
          transform: translateX(4px);
        }

        .profile-gallery-img {
          transition: transform 0.4s cubic-bezier(0.25,0.46,0.45,0.94), box-shadow 0.4s ease;
        }
        .profile-gallery-img:hover {
          transform: scale(1.03);
          box-shadow: 0 16px 50px rgba(0,0,0,0.6);
        }

        .profile-contact-action {
          transition: all 0.3s ease;
        }
        .profile-contact-action:hover {
          background: linear-gradient(135deg, rgba(200,170,110,0.15) 0%, rgba(200,170,110,0.05) 100%) !important;
          transform: scale(1.02);
        }
      `}</style>

      <FloatingParticles />

      {/* ══════════ HERO / COVER ══════════ */}
      <section className="relative min-h-screen flex flex-col items-center justify-end overflow-hidden pb-16">
        {/* Cover Image */}
        <div className="absolute inset-0">
          {config.cover_image ? (
            <img src={config.cover_image} alt="" className="w-full h-full object-cover" />
          ) : (
            <div
              className="w-full h-full"
              style={{
                background: "linear-gradient(160deg, #1a1a2e 0%, #0c0c0f 40%, #1a0f0a 100%)",
              }}
            />
          )}
          <div className="profile-cover-overlay absolute inset-0" />
          <div className="absolute inset-0" style={{ background: "rgba(12,12,15,0.3)" }} />
        </div>

        {/* Decorative corner accents */}
        <div
          className="absolute top-6 left-6"
          style={{
            width: 40,
            height: 40,
            borderLeft: "1px solid rgba(200,170,110,0.3)",
            borderTop: "1px solid rgba(200,170,110,0.3)",
          }}
        />
        <div
          className="absolute top-6 right-6"
          style={{
            width: 40,
            height: 40,
            borderRight: "1px solid rgba(200,170,110,0.3)",
            borderTop: "1px solid rgba(200,170,110,0.3)",
          }}
        />

        {/* Hero Content */}
        <div className="relative z-10 text-center px-6 w-full max-w-md">
          {/* Avatar */}
          {config.avatar && (
            <div className="relative mx-auto mb-8" style={{ width: 140, height: 140 }}>
              {/* Animated ring */}
              <div
                className="absolute rounded-full"
                style={{
                  inset: -6,
                  border: "1px dashed rgba(200,170,110,0.3)",
                  animation: "profile-avatar-ring 20s linear infinite",
                }}
              />
              <div
                className="rounded-full p-0.5"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(200,170,110,0.5), rgba(200,170,110,0.1), rgba(200,170,110,0.5))",
                  width: 140,
                  height: 140,
                }}
              >
                <Image
                  src={config.avatar}
                  alt="avatar"
                  width={136}
                  height={136}
                  preview={false}
                  className="rounded-full object-cover"
                  style={{ border: "3px solid #0c0c0f", display: "block" }}
                />
              </div>
            </div>
          )}

          {/* Name */}
          {config.full_name && (
            <h1
              className="profile-gold-text leading-tight mb-3"
              style={{
                fontFamily: "'Italiana', serif",
                fontSize: "clamp(2rem, 8vw, 2.8rem)",
                letterSpacing: "0.04em",
              }}
            >
              {config.full_name}
            </h1>
          )}

          {/* Title / Role */}
          {config.title && (
            <p
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "0.85rem",
                color: "rgba(255,255,255,0.45)",
                letterSpacing: "0.2em",
                textTransform: "uppercase",
              }}
            >
              {config.title}
            </p>
          )}

          {/* Scroll indicator */}
          <div className="mt-14 flex flex-col items-center gap-2">
            <span
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "0.65rem",
                letterSpacing: "0.2em",
                color: "rgba(255,255,255,0.3)",
                textTransform: "uppercase",
              }}
            >
              Khám phá
            </span>
            <div
              className="flex items-start justify-center"
              style={{
                width: 20,
                height: 32,
                borderRadius: 10,
                border: "1.5px solid rgba(200,170,110,0.25)",
                padding: 4,
              }}
            >
              <div
                style={{
                  width: 4,
                  height: 4,
                  borderRadius: "50%",
                  background: "rgba(200,170,110,0.7)",
                  animation: "profile-scroll-dot 1.5s ease-in-out infinite",
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ══════════ BIO ══════════ */}
      {config.bio && (
        <section className="relative py-14 px-4">
          <AnimatedSection>
            <div className="profile-glass rounded-3xl p-6 max-w-md mx-auto relative overflow-hidden">
              {/* Top accent line */}
              <div
                className="absolute top-0 left-0 w-full h-px"
                style={{ background: "linear-gradient(to right, transparent, rgba(200,170,110,0.25), transparent)" }}
              />

              <span
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  color: "rgba(200,170,110,0.5)",
                  fontSize: "0.7rem",
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  display: "block",
                  marginBottom: 12,
                }}
              >
                Giới thiệu
              </span>

              <Typography.Paragraph
                style={{
                  color: "rgba(255,255,255,0.7)",
                  marginBottom: 0,
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: "1.15rem",
                  lineHeight: 1.9,
                  fontStyle: "italic",
                }}
              >
                {config.bio}
              </Typography.Paragraph>

              {/* Bottom accent line */}
              <div
                className="absolute bottom-0 left-0 w-full h-px"
                style={{ background: "linear-gradient(to right, transparent, rgba(200,170,110,0.25), transparent)" }}
              />
            </div>
          </AnimatedSection>
        </section>
      )}

      {/* ══════════ CONTACT INFO ══════════ */}
      {(config.phone || config.email || config.address) && (
        <section className="relative py-10 px-4">
          <AnimatedSection className="text-center mb-6">
            <span
              style={{
                fontFamily: "'DM Sans', sans-serif",
                color: "rgba(200,170,110,0.5)",
                fontSize: "0.7rem",
                letterSpacing: "0.2em",
                textTransform: "uppercase",
              }}
            >
              Liên hệ
            </span>
            <Typography.Title
              level={4}
              style={{ color: "#fff", marginTop: 4, fontFamily: "'Italiana', serif", letterSpacing: "0.03em" }}
            >
              Thông Tin Liên Hệ
            </Typography.Title>
          </AnimatedSection>

          <AnimatedSection delay={100}>
            <div className="max-w-md mx-auto space-y-2">
              {config.phone && (
                <div
                  className="profile-glass profile-contact-action rounded-2xl px-5 py-3 cursor-pointer"
                  onClick={() => window.open(`tel:${config.phone}`)}
                >
                  <ContactItem icon="📞" value={config.phone} />
                </div>
              )}
              {config.email && (
                <div
                  className="profile-glass profile-contact-action rounded-2xl px-5 py-3 cursor-pointer"
                  onClick={() => window.open(`mailto:${config.email}`)}
                >
                  <ContactItem icon="✉️" value={config.email} />
                </div>
              )}
              {config.address && (
                <div className="profile-glass rounded-2xl px-5 py-3">
                  <ContactItem icon="📍" value={config.address} />
                </div>
              )}
            </div>
          </AnimatedSection>
        </section>
      )}

      <ElegantDivider />

      {/* ══════════ SOCIAL LINKS ══════════ */}
      {socialLinks.length > 0 && (
        <section className="relative py-10 px-4">
          <AnimatedSection className="text-center mb-6">
            <span
              style={{
                fontFamily: "'DM Sans', sans-serif",
                color: "rgba(200,170,110,0.5)",
                fontSize: "0.7rem",
                letterSpacing: "0.2em",
                textTransform: "uppercase",
              }}
            >
              Kết nối
            </span>
            <Typography.Title
              level={4}
              style={{ color: "#fff", marginTop: 4, fontFamily: "'Italiana', serif", letterSpacing: "0.03em" }}
            >
              Mạng Xã Hội
            </Typography.Title>
          </AnimatedSection>

          <AnimatedSection delay={100}>
            <div className="max-w-md mx-auto flex flex-col gap-3">
              {socialLinks.map((s) => (
                <SocialLink key={s.key} href={s.href!} icon={s.icon} label={s.label} />
              ))}
            </div>
          </AnimatedSection>
        </section>
      )}

      {/* ══════════ VIDEO ══════════ */}
      {config.intro_video && (
        <section className="relative py-12 px-4">
          <ElegantDivider />
          <AnimatedSection>
            <div className="text-center mb-6">
              <span
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  color: "rgba(200,170,110,0.5)",
                  fontSize: "0.7rem",
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                }}
              >
                Video
              </span>
              <Typography.Title level={4} style={{ color: "#fff", marginTop: 4, fontFamily: "'Italiana', serif" }}>
                Giới Thiệu
              </Typography.Title>
            </div>

            <div className="profile-glass rounded-3xl overflow-hidden max-w-md mx-auto">
              <video
                src={config.intro_video}
                controls
                playsInline
                className="w-full"
                style={{ aspectRatio: "16/9", objectFit: "cover" }}
                poster={config.cover_image}
              />
            </div>
          </AnimatedSection>
        </section>
      )}

      {/* ══════════ GALLERY ══════════ */}
      {config.gallery && config.gallery.length > 0 && (
        <section className="relative py-12 px-4">
          <ElegantDivider />

          <AnimatedSection className="text-center mb-8">
            <span
              style={{
                fontFamily: "'DM Sans', sans-serif",
                color: "rgba(200,170,110,0.5)",
                fontSize: "0.7rem",
                letterSpacing: "0.2em",
                textTransform: "uppercase",
              }}
            >
              Bộ sưu tập
            </span>
            <Typography.Title level={3} style={{ color: "#fff", marginTop: 4, fontFamily: "'Italiana', serif" }}>
              Khoảnh Khắc
            </Typography.Title>
            <div className="flex items-center justify-center gap-3 mt-2">
              <div className="h-px w-10" style={{ background: "rgba(200,170,110,0.3)" }} />
              <div
                style={{
                  width: 4,
                  height: 4,
                  borderRadius: "50%",
                  background: "rgba(200,170,110,0.5)",
                }}
              />
              <div className="h-px w-10" style={{ background: "rgba(200,170,110,0.3)" }} />
            </div>
          </AnimatedSection>

          {/* Masonry Gallery */}
          <div className="max-w-md mx-auto columns-2 gap-3" style={{ columnGap: 10 }}>
            {config.gallery.map((img, i) => (
              <AnimatedSection key={i} delay={i * 100} className="mb-3 break-inside-avoid">
                <div
                  className="profile-gallery-img rounded-2xl overflow-hidden"
                  style={{ border: "1px solid rgba(255,255,255,0.06)" }}
                >
                  <Image
                    src={img}
                    alt={`gallery-${i}`}
                    className="w-full object-cover"
                    style={{
                      aspectRatio: i % 3 === 0 ? "3/4" : i % 3 === 1 ? "1/1" : "4/5",
                      display: "block",
                    }}
                  />
                </div>
              </AnimatedSection>
            ))}
          </div>
        </section>
      )}

      {/* ══════════ FOOTER ══════════ */}
      <footer className="relative py-10 px-4 text-center" style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }}>
        <AnimatedSection className="flex flex-col items-center">
          {/* Mini avatar */}
          {config.avatar && (
            <div className="mb-3" style={{ opacity: 0.4 }}>
              <img
                src={config.avatar}
                alt=""
                className="rounded-full object-cover"
                style={{ width: 32, height: 32, border: "1px solid rgba(200,170,110,0.2)" }}
              />
            </div>
          )}
          {config.full_name && (
            <Typography.Text
              style={{
                color: "rgba(255,255,255,0.25)",
                fontSize: "0.8rem",
                fontFamily: "'Italiana', serif",
                letterSpacing: "0.1em",
              }}
            >
              {config.full_name}
            </Typography.Text>
          )}
          <Typography.Text
            style={{
              color: "rgba(255,255,255,0.12)",
              fontSize: "0.7rem",
              fontFamily: "'DM Sans', sans-serif",
              marginTop: 4,
              display: "block",
            }}
          >
            Powered by Miraway
          </Typography.Text>
        </AnimatedSection>
      </footer>
    </div>
  );
};

export default PublicProfile;
